import {
  Field,
  FilterCombobox,
} from '@/features/reports/components/shared/filter-combobox.component';
import type { FilterOption } from '@/features/reports/hooks/use-reports-filter-options.hook';
import { DateRangeFilterField } from '@/shared/ui';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const beneficiaryTypeOptions: FilterOption[] = [
  { label: 'Todos', value: '' },
  { label: 'Assessoria', value: 'advisory' },
  { label: 'Corretor', value: 'broker' },
];

const searchTypeOptions: FilterOption[] = [
  { label: 'Recibo', value: 'receipt' },
  { label: 'Valor', value: 'value' },
  { label: 'Status', value: 'status' },
];

const statusOptions: FilterOption[] = [
  { label: 'Todos', value: '' },
  { label: 'Pendente', value: 'pending' },
  { label: 'Pagos', value: 'paid' },
];

const MONTHS_MIN = 1;
const MONTHS_MAX = 12;

const clampMonths = (value: number) =>
  Math.min(MONTHS_MAX, Math.max(MONTHS_MIN, value));

export type ReceiptsFilterValues = {
  broker: string;
  advisory: string;
  beneficiaryType: string;
  searchType: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  months: number;
};

type ReceiptsFiltersProps = {
  /** Painel aberto/fechado (controla a animação de abrir/fechar). */
  open: boolean;
  /** Perfil Seguradora: habilita Assessoria e Tipo de Favorecido. */
  isInsurer: boolean;
  /** Exibe o Corretor (Seguradora + Assessoria). */
  showBroker: boolean;
  /** Opções reais (PartyCode) — grade comercial do usuário. */
  advisoryOptions: FilterOption[];
  brokerOptions: FilterOption[];
  values: ReceiptsFilterValues;
  // eslint-disable-next-line no-unused-vars
  onChange: (_patch: Partial<ReceiptsFilterValues>) => void;
  onClear: () => void;
};

// Rótulo do campo Meses (não é select) — igual ao do Field.
const FIELD_LABEL_CLASS = 'text-xs font-semibold text-alba-black-500';

// Slider customizado com posição CONTÍNUA (step="any"): o thumb acompanha o
// ponteiro sem pular entre os passos e sem "assentar" no valor ao soltar. O mês
// é apenas arredondado a partir da posição. O `pos` interno só é ressincronizado
// quando o valor muda por fora (ex.: botões +/- ou Período), nunca durante o arraste.
function MonthsSlider({
  value,
  min,
  max,
  onValueChange,
}: {
  value: number;
  min: number;
  max: number;
  // eslint-disable-next-line no-unused-vars
  onValueChange: (_next: number) => void;
}) {
  const range = max > min ? max - min : 1;
  const valueToPos = (v: number) => ((v - min) / range) * 100;
  const posToValue = (p: number) =>
    Math.min(max, Math.max(min, Math.round(min + (p / 100) * range)));

  const [pos, setPos] = useState(() => valueToPos(value));

  useEffect(() => {
    if (posToValue(pos) !== value) {
      setPos(valueToPos(value));
    }
  }, [value]);

  const handleInput = (rawPos: number) => {
    setPos(rawPos);
    const nextValue = posToValue(rawPos);
    if (nextValue !== value) onValueChange(nextValue);
  };

  return (
    <div className='relative flex h-11 items-center'>
      <div className='relative h-1.5 w-full rounded-full bg-alba-gray-300'>
        <div
          className='absolute inset-y-0 left-0 rounded-full bg-alba-blue-600'
          style={{ width: `${pos}%` }}
        />
        <div
          className='absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-alba-blue-600 shadow'
          style={{ left: `${pos}%` }}
        />
      </div>
      <input
        type='range'
        min={0}
        max={100}
        step='any'
        value={pos}
        onChange={event => handleInput(Number(event.target.value))}
        aria-label='Quantidade de meses'
        className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
      />
    </div>
  );
}

export function ReceiptsFilters({
  open,
  isInsurer,
  showBroker,
  advisoryOptions,
  brokerOptions,
  values,
  onChange,
  onClear,
}: ReceiptsFiltersProps) {
  const months = values.months || MONTHS_MIN;
  const hasActiveFilter =
    Boolean(
      values.broker ||
      values.advisory ||
      values.beneficiaryType ||
      values.searchType ||
      values.status
    ) || months !== MONTHS_MIN;

  return (
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className='overflow-hidden'>
        <div className='mt-3 rounded-3xl bg-[#E7E8E9]/50 px-6 py-4'>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5'>
            {/* Campos exclusivos do perfil Seguradora */}
            {isInsurer && (
              <Field label='Assessoria'>
                <FilterCombobox
                  placeholder='Selecione a assessoria'
                  options={advisoryOptions}
                  value={values.advisory}
                  onChange={next => onChange({ advisory: next })}
                />
              </Field>
            )}

            {isInsurer && (
              <Field label='Tipo de Favorecido'>
                <FilterCombobox
                  placeholder='Selecione o tipo'
                  options={beneficiaryTypeOptions}
                  value={values.beneficiaryType}
                  onChange={next => onChange({ beneficiaryType: next })}
                />
              </Field>
            )}

            {showBroker && (
              <Field label='Corretor'>
                <FilterCombobox
                  placeholder='Selecione o corretor'
                  options={brokerOptions}
                  value={values.broker}
                  onChange={next => onChange({ broker: next })}
                />
              </Field>
            )}

            <Field label='Tipo da Pesquisa'>
              <FilterCombobox
                placeholder='Selecione o tipo'
                options={searchTypeOptions}
                value={values.searchType}
                onChange={next => onChange({ searchType: next })}
              />
            </Field>

            <Field label='Status'>
              <FilterCombobox
                placeholder='Selecione o status'
                options={statusOptions}
                value={values.status}
                onChange={next => onChange({ status: next })}
              />
            </Field>

            <div className='flex flex-col gap-4 sm:flex-row sm:items-end md:col-span-2 lg:col-span-4'>
              <Field label='Período'>
                <DateRangeFilterField
                  from={values.dateFrom}
                  to={values.dateTo}
                  placeholder='Período'
                  onChange={range =>
                    onChange({ dateFrom: range.from, dateTo: range.to })
                  }
                />
              </Field>

              {/* Meses — colado ao Período (slider mais curto) */}
              <div className='flex flex-col gap-1'>
                <label className={FIELD_LABEL_CLASS}>
                  Meses{' '}
                  <span className='font-normal text-alba-gray-600'>
                    ({months})
                  </span>
                </label>
                <div className='flex items-center gap-3'>
                  <div className='w-48'>
                    <MonthsSlider
                      value={months}
                      min={MONTHS_MIN}
                      max={MONTHS_MAX}
                      onValueChange={next =>
                        onChange({ months: clampMonths(next) })
                      }
                    />
                  </div>
                  <div className='relative w-24 shrink-0'>
                    <input
                      type='number'
                      min={MONTHS_MIN}
                      max={MONTHS_MAX}
                      value={months}
                      onChange={event =>
                        onChange({
                          months: clampMonths(
                            Number(event.target.value) || MONTHS_MIN
                          ),
                        })
                      }
                      aria-label='Quantidade de meses'
                      className='h-11 w-full rounded-md border border-alba-gray-300 bg-white pl-3 pr-7 text-sm text-alba-black-500 focus:border-alba-blue-500 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                    />
                    <div className='absolute right-1 top-1/2 flex -translate-y-1/2 flex-col'>
                      <button
                        type='button'
                        aria-label='Aumentar meses'
                        onClick={() =>
                          onChange({ months: clampMonths(months + 1) })
                        }
                        className='flex h-4 w-5 items-center justify-center rounded text-alba-gray-600 hover:text-alba-black-500'
                      >
                        <ChevronUp className='h-3.5 w-3.5' strokeWidth={2} />
                      </button>
                      <button
                        type='button'
                        aria-label='Diminuir meses'
                        onClick={() =>
                          onChange({ months: clampMonths(months - 1) })
                        }
                        className='flex h-4 w-5 items-center justify-center rounded text-alba-gray-600 hover:text-alba-black-500'
                      >
                        <ChevronDown className='h-3.5 w-3.5' strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='flex items-end md:col-span-2 lg:col-span-1'>
              {hasActiveFilter && (
                <button
                  type='button'
                  onClick={onClear}
                  className='flex h-11 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-alba-blue-600 transition-colors hover:bg-alba-gray-100'
                >
                  <X className='h-4 w-4' strokeWidth={1.8} />
                  Limpar Seleção
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
